import { RefObject, useCallback, useLayoutEffect } from 'react';

/**
 * The last scroll position of the sidebar list, for this page load.
 *
 * Every page renders its own `SMLContainer`, so a click in the sidebar mounts a new sidebar, and
 * a new list starts at the top. A reader who clicked an item low in Promote then lost the item
 * from view. The value lives outside React so that it outlives the sidebar that wrote it.
 */
let savedScrollTop = 0;

/** For tests: forget the position, as a new page load does. */
export function resetSidebarScrollMemory(): void {
  savedScrollTop = 0;
}

/**
 * Keeps the sidebar list at the same scroll position from one page to the next. After the
 * restore, the active item is scrolled into view if it is still hidden (for example, a deep
 * link opened a page low in the list).
 */
export function useSidebarScrollMemory(listRef: RefObject<HTMLElement | null>): { onScroll: () => void } {
  useLayoutEffect(() => {
    const list = listRef.current;
    if (list === null) {
      return;
    }
    list.scrollTop = savedScrollTop;

    const active = list.querySelector<HTMLElement>('[aria-current="page"]');
    if (active === null) {
      return;
    }
    const listBox = list.getBoundingClientRect();
    const activeBox = active.getBoundingClientRect();
    if (activeBox.top < listBox.top || activeBox.bottom > listBox.bottom) {
      active.scrollIntoView({ block: 'nearest' });
      savedScrollTop = list.scrollTop;
    }
  }, [listRef]);

  const onScroll = useCallback(() => {
    const list = listRef.current;
    if (list !== null) {
      savedScrollTop = list.scrollTop;
    }
  }, [listRef]);

  return { onScroll };
}
